import type { Language } from 'codegen/types'

import { studioSelectableLanguageMap } from './constants'

/**
 * Studio locales keyed by their lowercased enum value, which is also the
 * primary subtag of the matching browser tag for every locale except the
 * Chinese script variants.
 */
const SELECTABLE_LANGUAGE_BY_KEY = new Map<string, Language>(
  [...studioSelectableLanguageMap.keys()].map(language => [language.toLowerCase(), language]),
)

/**
 * Primary subtags browsers send that do not match the locale we ship them as.
 * Norwegian is requested as Bokmål or Nynorsk rather than the macrolanguage,
 * and `in` is the pre-1989 code some user agents still send for Indonesian.
 */
const LANGUAGE_KEY_BY_SUBTAG_ALIAS: Record<string, string> = {
  nb: 'no',
  nn: 'no',
  in: 'id',
}

/** Regions that write traditional Chinese; every other region gets simplified. */
const TRADITIONAL_CHINESE_SUBTAGS = new Set(['hant', 'tw', 'hk', 'mo'])

function toChineseLanguageKey(subtags: readonly string[]): string {
  return subtags.some(subtag => TRADITIONAL_CHINESE_SUBTAGS.has(subtag)) ? 'zh_hant' : 'zh_hans'
}

function toSelectableStudioLanguage(browserTag: string): Language | undefined {
  const [primarySubtag = '', ...subtags] = browserTag.toLowerCase().split('-')

  const languageKey =
    primarySubtag === 'zh'
      ? toChineseLanguageKey(subtags)
      : (LANGUAGE_KEY_BY_SUBTAG_ALIAS[primarySubtag] ?? primarySubtag)

  return SELECTABLE_LANGUAGE_BY_KEY.get(languageKey)
}

/**
 * The first browser tag the Studio language picker can actually switch to, in
 * the browser's own order of preference.
 *
 * Regions are dropped rather than matched, because we ship one bundle per
 * language: `pt-PT` resolves to the same Portuguese the picker offers. Tags we
 * do not offer are skipped instead of ending the search, so a Russian-first
 * user whose second choice is German still resolves to German.
 */
export function resolveSelectableStudioLanguage(
  browserTags: readonly string[],
): Language | undefined {
  for (const browserTag of browserTags) {
    const language = toSelectableStudioLanguage(browserTag)

    if (language) {
      return language
    }
  }

  return undefined
}

/**
 * The languages this browser asks for, most preferred first. `navigator.languages`
 * is the ordered list behind `Accept-Language`; `navigator.language` is the
 * single-value fallback for user agents that omit it.
 */
export function getBrowserPreferredLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') {
    return []
  }

  if (navigator.languages?.length) {
    return navigator.languages
  }

  return navigator.language ? [navigator.language] : []
}
